/**
 * Mock Supabase Client for Development
 * ใช้เมื่อไม่สามารถเชื่อมต่อ Supabase ได้
 */

// Mock data
const mockVehicles = [
  {
    id: '1',
    registration_number: 'ABC-1234',
    vehicle_type: 'รถตู้',
    brand_model: 'Toyota Hiace',
    seating_capacity: 12,
    status: 'available',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: '2',
    registration_number: 'DEF-5678',
    vehicle_type: 'รถบัส',
    brand_model: 'Isuzu NPR',
    seating_capacity: 25,
    status: 'in_use',
    created_at: '2024-01-02T00:00:00Z',
    updated_at: '2024-01-02T00:00:00Z'
  }
]

const mockDrivers = [
  {
    id: '1',
    full_name: 'สมชาย ใจดี',
    phone_number: '081-234-5678',
    status: 'available',
    line_user_id: 'Ufba3f39a9a64ac32739942de903da3db',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: '2',
    full_name: 'สมหญิง รักงาน',
    phone_number: '082-345-6789',
    status: 'unavailable',
    line_user_id: null,
    created_at: '2024-01-02T00:00:00Z',
    updated_at: '2024-01-02T00:00:00Z'
  }
]

const mockScheduleEntries = [
  {
    id: '1',
    usage_date: '2024-12-17',
    start_time: '08:00',
    end_time: '17:00',
    vehicle_id: '1',
    driver_id: '1',
    notes: 'เดินทางไปจังหวัดนครปฐม',
    created_at: '2024-12-16T00:00:00Z',
    updated_at: '2024-12-16T00:00:00Z'
  }
]

const mockSettings = [
  {
    id: '1',
    key: 'realtime_enabled',
    value: true,
    description: 'Enable/disable realtime features globally',
    category: 'realtime',
    is_encrypted: false,
    created_at: '2024-12-16T00:00:00Z',
    updated_at: '2024-12-16T00:00:00Z'
  },
  {
    id: '2',
    key: 'realtime_auto_reconnect',
    value: true,
    description: 'Automatically reconnect when connection is lost',
    category: 'realtime',
    is_encrypted: false,
    created_at: '2024-12-16T00:00:00Z',
    updated_at: '2024-12-16T00:00:00Z'
  },
  {
    id: '3',
    key: 'realtime_max_reconnect_attempts',
    value: 3,
    description: 'Maximum number of reconnection attempts',
    category: 'realtime',
    is_encrypted: false,
    created_at: '2024-12-16T00:00:00Z',
    updated_at: '2024-12-16T00:00:00Z'
  },
  {
    id: '4',
    key: 'realtime_reconnect_delay',
    value: 2000,
    description: 'Delay between reconnection attempts in milliseconds',
    category: 'realtime',
    is_encrypted: false,
    created_at: '2024-12-16T00:00:00Z',
    updated_at: '2024-12-16T00:00:00Z'
  },
  {
    id: '5',
    key: 'ui_theme',
    value: '"light"',
    description: 'Application theme (light/dark)',
    category: 'ui',
    is_encrypted: false,
    created_at: '2024-12-16T00:00:00Z',
    updated_at: '2024-12-16T00:00:00Z'
  },
  {
    id: '6',
    key: 'ui_language',
    value: '"th"',
    description: 'Application language',
    category: 'ui',
    is_encrypted: false,
    created_at: '2024-12-16T00:00:00Z',
    updated_at: '2024-12-16T00:00:00Z'
  },
  {
    id: '7',
    key: 'ui_show_connection_status',
    value: true,
    description: 'Show connection status indicator',
    category: 'ui',
    is_encrypted: false,
    created_at: '2024-12-16T00:00:00Z',
    updated_at: '2024-12-16T00:00:00Z'
  },
  {
    id: '8',
    key: 'line_notifications_enabled',
    value: false,
    description: 'Enable/disable LINE notifications globally',
    category: 'line',
    is_encrypted: false,
    created_at: '2024-12-16T00:00:00Z',
    updated_at: '2024-12-16T00:00:00Z'
  },
  {
    id: '9',
    key: 'line_channel_access_token',
    value: '""',
    description: 'LINE Channel Access Token',
    category: 'line',
    is_encrypted: true,
    created_at: '2024-12-16T00:00:00Z',
    updated_at: '2024-12-16T00:00:00Z'
  }
]

// Mock database collections
const mockData = {
  vehicles: [...mockVehicles],
  drivers: [...mockDrivers],
  schedule_entries: [...mockScheduleEntries],
  settings: [...mockSettings]
}

// Mock query builder
class MockQueryBuilder {
  private table: string
  private selectFields: string = '*'
  private whereConditions: any[] = []
  private limitValue?: number
  private orderByField?: string
  private orderByAscending: boolean = true

  constructor(table: string) {
    this.table = table
  }

  select(fields: string = '*') {
    this.selectFields = fields
    return this
  }

  eq(field: string, value: any) {
    this.whereConditions.push({ field, operator: 'eq', value })
    return this
  }

  in(field: string, values: any[]) {
    this.whereConditions.push({ field, operator: 'in', value: values })
    return this
  }

  limit(count: number) {
    this.limitValue = count
    return this
  }

  order(field: string, options?: { ascending?: boolean }) {
    this.orderByField = field
    this.orderByAscending = options?.ascending ?? true
    return this
  }

  single() {
    return this.execute(true)
  }

  async execute(single: boolean = false) {
    await new Promise(resolve => setTimeout(resolve, 100)) // Simulate network delay

    let data = [...(mockData[this.table as keyof typeof mockData] || [])]

    // Apply where conditions
    this.whereConditions.forEach(condition => {
      if (condition.operator === 'eq') {
        data = data.filter(item => item[condition.field] === condition.value)
      } else if (condition.operator === 'in') {
        data = data.filter(item => condition.value.includes(item[condition.field]))
      }
    })

    // Apply ordering
    if (this.orderByField) {
      data.sort((a, b) => {
        const aVal = a[this.orderByField!]
        const bVal = b[this.orderByField!]
        const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
        return this.orderByAscending ? comparison : -comparison
      })
    }

    // Apply limit
    if (this.limitValue) {
      data = data.slice(0, this.limitValue)
    }

    // Handle single query
    if (single) {
      const result = data[0] || null
      return {
        data: result,
        error: result ? null : { code: 'PGRST116', message: 'No rows found' },
        count: data.length
      }
    }

    return {
      data,
      error: null,
      count: data.length
    }
  }

  // For compatibility with Supabase client
  then(onResolve: any, onReject?: any) {
    return this.execute().then(onResolve, onReject)
  }
}

// Mock insert/update/delete operations
class MockMutationBuilder {
  private table: string
  private operation: 'insert' | 'update' | 'delete'
  private data?: any
  private whereConditions: any[] = []

  constructor(table: string, operation: 'insert' | 'update' | 'delete') {
    this.table = table
    this.operation = operation
  }

  eq(field: string, value: any) {
    this.whereConditions.push({ field, operator: 'eq', value })
    return this
  }

  select(fields: string = '*') {
    return this
  }

  single() {
    return this.execute(true)
  }

  async execute(single: boolean = false) {
    await new Promise(resolve => setTimeout(resolve, 150)) // Simulate network delay

    const tableData = mockData[this.table as keyof typeof mockData] as any[]

    if (this.operation === 'insert') {
      const newItem = {
        ...this.data,
        id: String(Date.now()),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
      tableData.push(newItem)
      
      return {
        data: single ? newItem : [newItem],
        error: null
      }
    }

    if (this.operation === 'update') {
      const updatedItems: any[] = []
      
      this.whereConditions.forEach(condition => {
        if (condition.operator === 'eq') {
          const index = tableData.findIndex(item => item[condition.field] === condition.value)
          if (index !== -1) {
            tableData[index] = {
              ...tableData[index],
              ...this.data,
              updated_at: new Date().toISOString()
            }
            updatedItems.push(tableData[index])
          }
        }
      })

      return {
        data: single ? updatedItems[0] || null : updatedItems,
        error: null
      }
    }

    if (this.operation === 'delete') {
      const deletedItems: any[] = []
      
      this.whereConditions.forEach(condition => {
        if (condition.operator === 'eq') {
          const index = tableData.findIndex(item => item[condition.field] === condition.value)
          if (index !== -1) {
            deletedItems.push(tableData[index])
            tableData.splice(index, 1)
          }
        }
      })

      return {
        data: single ? deletedItems[0] || null : deletedItems,
        error: null
      }
    }

    return { data: null, error: new Error('Unknown operation') }
  }

  // For compatibility with Supabase client
  then(onResolve: any, onReject?: any) {
    return this.execute().then(onResolve, onReject)
  }
}

// Mock Supabase client
export const mockSupabase = {
  from(table: string) {
    return {
      select: (fields?: string, options?: any) => {
        const builder = new MockQueryBuilder(table)
        return builder.select(fields || '*')
      },
      
      insert: (data: any) => {
        const builder = new MockMutationBuilder(table, 'insert')
        builder['data'] = data
        return builder
      },
      
      update: (data: any) => {
        const builder = new MockMutationBuilder(table, 'update')
        builder['data'] = data
        return builder
      },
      
      delete: () => {
        return new MockMutationBuilder(table, 'delete')
      },

      upsert: (data: any) => {
        // Simple upsert implementation
        const builder = new MockMutationBuilder(table, 'insert')
        builder['data'] = data
        return builder
      }
    }
  },

  // Mock realtime
  channel: (name: string) => ({
    on: (event: string, config: any, callback: Function) => ({
      subscribe: (statusCallback?: Function) => {
        // Simulate successful subscription
        setTimeout(() => {
          if (statusCallback) {
            statusCallback('SUBSCRIBED')
          }
        }, 100)
        
        return {
          unsubscribe: () => {
            console.log('Mock realtime unsubscribed')
          }
        }
      }
    })
  })
}

// Export types for compatibility
export type Database = {
  public: {
    Tables: {
      vehicles: any
      drivers: any
      schedule_entries: any
      settings: any
    }
  }
}